import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation } from "react-router-dom";
import { Radio } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { ReactComponent as IconError } from "src/assets/icons/toast-error.svg";
import { ReactComponent as IconSuccess } from "src/assets/icons/toast-success.svg";
import { ReactComponent as IconWarning } from "src/assets/icons/toast-warning.svg";
import { fnoActivationOptions, stepAndRouteMap } from 'src/constants';
import { ScreenLoader } from "src/components/screenLoader"
import useCleverTapEvents from 'src/events/useGetEvents';
import { FNO_EVENT } from 'src/events/constant';
import { RadioSelect } from '../../components';
import { OnboardingProgress } from '../../components/onboardingProgress';
import { RootState } from '../../redux/store';
import { useStyles } from './incomeProofForm.styles';
import {
  clearFnoActivationFormError, startSetuVerification, checkSetuVerificationStatus,
} from './fnoActivationFormSlice';
import { getProgressStepper } from '../../utils/helpers';
import { getUserDetails } from '../account/accountSlice';
import { Button } from '../../components/atoms';

interface ISetuFlowProps {
  handleChangeSetuFlow: () => void;
}

const API_CALL_INTERVAL = 3000;
const TIMER = 300;

const SetuFlow = ({
  handleChangeSetuFlow,
}: ISetuFlowProps) => {
  const { classes, cx } = useStyles();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { TriggerEvent } = useCleverTapEvents();
  const userDetails = useSelector((state: RootState) => state.account.userDetails);
  const fnoActivationDetails = useSelector((state: RootState) => state.fnoActivationDetails);
  const location = useLocation();
  const currentUrl = new URL(window.location.href);
  const consentId = currentUrl.searchParams.get("id");
  const [verificationType, setVerificationType] = useState<string>("pennyDrop");
  const [timer, setTimer] = useState(TIMER);
  const isSetuVerificationInProgress = location.pathname === "/income-proof/callback" && consentId

  const handleClickNext = () => {
    if (verificationType === "pennyDrop") {
      dispatch(startSetuVerification());
    } else if (verificationType === "manual") {
      handleChangeSetuFlow()
    } else {
      notifications.show({
        color: 'red',
        message: 'Please select a verification type!',
        icon: <IconError />,
      });
    }
  }

  const navigateToIncomeProofHome = () => {
    window.history.pushState({}, "/", "/");
    navigate(stepAndRouteMap[userDetails.nextStep][0]);
  }

  const handleSetVerificationType = (value: string) => {
    setVerificationType(value)
    TriggerEvent(FNO_EVENT.enableFnO, { option: value });
  }

  // setting a timer for 5 minutes, if success doesn't come in 5min, then api calls will stop and error will be shown
  useEffect(() => {
    const interval = setInterval(() => {
      if (isSetuVerificationInProgress) {
        if (timer > 0) {
          setTimer((timerValue) => (timerValue > 0 ? timerValue - 1 : 0));
        } else {
          clearInterval(interval);
          notifications.show({
            color: 'red',
            message: 'Income proof verification failed, Try again!',
            icon: <IconError />,
          })
          navigateToIncomeProofHome()
        }
      }
    }, 1000);
    return () => {
      clearInterval(interval);
    };
  }, [timer]);

  useEffect(() => {
    const interval = setInterval(() => {
      if (isSetuVerificationInProgress) {
        // if it gets success, then will fetch user details and clear timer
        if (fnoActivationDetails.status === 'success') {
          notifications.show({
            color: 'green',
            message: 'Income proof verified successfully',
            icon: <IconSuccess />,
          })
          clearInterval(interval);
          dispatch(getUserDetails())
          TriggerEvent(FNO_EVENT.enableFnoSetu, { name: userDetails.name });
          // @ts-ignore
        } else if (fnoActivationDetails.status === 'failed') {
          // incase of failure, stopping api calls, user needs to start again
          clearInterval(interval);
        } else {
          // otherwise keep calling api
          dispatch(checkSetuVerificationStatus({ consentId }));
        }
      }
    }, API_CALL_INTERVAL);
    return () => {
      clearInterval(interval);
    };
  }, [fnoActivationDetails.status]);

  useEffect(() => {
    if (fnoActivationDetails.status === 'success') {
      navigateToIncomeProofHome()
      // @ts-ignore
    } else if (fnoActivationDetails.status === 'failed') {
      notifications.show({
        color: 'red',
        message: 'Income proof verification failed, Try again!',
        icon: <IconError />,
      })
      navigateToIncomeProofHome()
    } else if (fnoActivationDetails.error.isError) {
      navigateToIncomeProofHome();
    }
  }, [userDetails, fnoActivationDetails.status, fnoActivationDetails.error.isError]);

  useEffect(() => {
    if (fnoActivationDetails.setuRedirectionUrl) {
      window.location.href = fnoActivationDetails.setuRedirectionUrl;
    }
  }, [fnoActivationDetails.setuRedirectionUrl]);

  useEffect(() => {
    if (fnoActivationDetails.status === 'success') {
      if (fnoActivationDetails.fnoSkipped) {
        notifications.show({
          color: 'orange',
          message: 'Fno Trade Activation Skipped!',
          icon: <IconWarning />,
        });
      }
    } else if (fnoActivationDetails.status === 'error') {
      TriggerEvent(FNO_EVENT.enableFnoError, {
        errorMessage: fnoActivationDetails.error.message || "",
        name: userDetails.name,
      });
      dispatch(clearFnoActivationFormError());
      notifications.show({
        color: 'red',
        message: fnoActivationDetails.error.message || 'Could\'nt save income details, Try again!',
        icon: <IconError />,
      });
    }
  }, [fnoActivationDetails.status]);
  return (
    <>
      <div className="csMainContainer">
        <div className="csHeader">
          <div className="csProgressWrapper">
            <OnboardingProgress
              progress={getProgressStepper(userDetails.currentStep)}
            />
          </div>
        </div>
        <div className={classes.setuContentContainer}>
          <h2 className="csHeading">Enable F&O trading</h2>
          <p className="csDescription">
            Verify your income proof to enable FnO trading on your account.
          </p>
          <Radio.Group
            name="bank account verification"
            value={verificationType}
            onChange={(value) => handleSetVerificationType(value)}
          >
            {fnoActivationOptions.map((item) => (
              <RadioSelect
                key={item.value}
                value={item.value}
                label={item.title}
                description={item.description}
                image={item.image}
                active={verificationType === item.value}
                badgeValue={item.badge}
              />
            ))}
          </Radio.Group>
        </div>
        <div className={cx("csFooter")}>
          <Button onClick={handleClickNext}>
            Next
          </Button>
        </div>
      </div>
      {location.pathname === "/income-proof/callback" ? <ScreenLoader /> : null}
    </>
  );
};

export default SetuFlow;
